import { FC } from "react";
import { Box, Card, Stack, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";

import { useIsPersian } from "../hooks/isPersian";

type CustomTimeLinePropsType = {
  list: { title: string; date: string; text?: string; list?: string[] }[];
};

export const CustomTimeLine: FC<CustomTimeLinePropsType> = ({ list }) => {
  const isPersian = useIsPersian();
  const { t } = useTranslation();

  return (
    <Stack spacing={2.2} sx={{ position: "relative" }}>
      {list.map((item, index) => {
        const isLast = index === list.length - 1;

        return (
          <Box
            key={`${item.title}-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns: isPersian ? "1fr 34px" : "34px 1fr",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            {!isPersian && <TimelineMarker isLast={isLast} />}

            <Card
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "background.surface",
                backdropFilter: "blur(14px)",
                borderColor: "divider",
                boxShadow: "sm",
              }}
            >
              <Stack spacing={0.9}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "primary.softBg",
                    }}
                  >
                    <Typography level="body-xs" sx={{ lineHeight: 1 }}>
                      📅
                    </Typography>
                  </Box>
                  <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                    {t(item.date)}
                  </Typography>
                </Stack>

                <Typography
                  level="title-md"
                  textAlign={isPersian ? "right" : "left"}
                  noWrap
                >
                  {t(item.title)}
                </Typography>

                {item.list ? (
                  <Stack spacing={0.6}>
                    {item.list.map((listItem, listIndex) => (
                      <Typography
                        key={`${listItem}-${listIndex}`}
                        level="body-sm"
                        textAlign={isPersian ? "right" : "left"}
                        sx={{ color: "text.secondary" }}
                      >
                        • {t(listItem)}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    level="body-sm"
                    textAlign={isPersian ? "right" : "left"}
                    sx={{ color: "text.secondary" }}
                  >
                    {t(item.text || "")}
                  </Typography>
                )}
              </Stack>
            </Card>

            {isPersian && <TimelineMarker isLast={isLast} />}
          </Box>
        );
      })}
    </Stack>
  );
};

const TimelineMarker = ({ isLast }: { isLast: boolean }) => {
  return (
    <Stack alignItems="center" sx={{ pt: 2 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "primary.500",
          boxShadow: "0 0 0 6px rgba(24,93,231,0.14)",
        }}
      />
      {!isLast && (
        <Box
          sx={{
            width: 2,
            flex: 1,
            minHeight: 70,
            mt: 1,
            borderRadius: 999,
            bgcolor: "primary.300",
            opacity: 0.65,
          }}
        />
      )}
    </Stack>
  );
};
